import type { ButtonHTMLAttributes, CSSProperties } from 'react';
import { useState } from 'react';

type Variant = 'primary' | 'ghost' | 'danger';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
}

/**
 * Amassa Redesign v3 button.
 *
 *  - `primary`  — pill, accent-tinted background + soft hover shadow.
 *                 The accent resolves from the nearest `data-mod` ancestor
 *                 (rail item, dashboard, module page).
 *  - `ghost`    — transparent, hairline on hover. Used in topbar.
 *  - `danger`   — coral/salmon pill for destructive actions.
 *
 * Hover uses the soft accent-tinted shadow formula from hub-modern.css §8,
 * not the BQB hard-shadow.
 */
export function Button({
  variant = 'primary',
  style,
  className,
  ...rest
}: Props) {
  const [hover, setHover] = useState(false);

  // A disabled button had NO visual state at all before 2026-09-09.
  // `disabled` rode through `{...rest}` to the DOM, so the browser correctly
  // dropped the click — but every style here is INLINE, and inline styles beat
  // any stylesheet, so `cursor: pointer` and the full-strength accent fill kept
  // rendering. There is no `.hub-btn:disabled` rule anywhere in the app either.
  // The result: a dead button that looks completely live. That is exactly how
  // the Import Logs "continue" button read to the ops team — they clicked a
  // bright orange CTA and nothing happened, with no error and no request.
  // Every one of the ~159 pages inherits this component, so this is fixed
  // here rather than at the call site.
  const isDisabled = rest.disabled === true;
  const isHovered = hover && !isDisabled;

  const base: CSSProperties = {
    fontFamily: 'var(--hm-mono)',
    fontWeight: 600,
    fontSize: 12,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    padding: variant === 'ghost' ? '6px 10px' : '8px 16px',
    border: '1px solid transparent',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    transition:
      'transform 80ms ease, box-shadow 160ms ease, background 160ms ease, border-color 160ms ease, color 160ms ease',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  };

  // Desaturate rather than just fade: at 55% opacity a solid accent pill is
  // still bright enough on the ink ground to read as "primary CTA". Dropping
  // the shadow is what actually removes the raised, pressable affordance.
  const disabledStyle: CSSProperties = {
    opacity: 0.45,
    filter: 'saturate(0.55)',
    boxShadow: 'none',
    pointerEvents: 'auto', // keep the cursor + any title tooltip readable
  };

  const variants: Record<Variant, CSSProperties> = {
    // Filled accent CTA — the accent resolves from the nearest data-mod
    // ancestor. Dark ink reads cleanly on every mid-bright module accent.
    primary: {
      borderRadius: 'var(--hm-radius-pill)',
      fontWeight: 700,
      background: isHovered
        ? 'color-mix(in oklab, var(--accent, var(--brand)) 86%, white)'
        : 'var(--accent, var(--brand))',
      color: 'var(--hm-btn-ink)',
      borderColor: 'transparent',
      boxShadow: isHovered
        ? '0 16px 34px -14px color-mix(in oklab, var(--accent, var(--brand)) 72%, transparent)'
        : '0 2px 10px -4px color-mix(in oklab, var(--accent, var(--brand)) 58%, transparent)',
    },
    ghost: {
      borderRadius: 'var(--hm-radius)',
      background: isHovered ? 'var(--bg-card-hover)' : 'transparent',
      color: isHovered ? 'var(--text)' : 'var(--text-muted)',
      borderColor: isHovered ? 'var(--border-strong)' : 'var(--border)',
    },
    danger: {
      borderRadius: 'var(--hm-radius-pill)',
      fontWeight: 700,
      background: isHovered
        ? 'color-mix(in oklab, var(--heat) 86%, white)'
        : 'var(--heat)',
      color: '#fff',
      borderColor: 'transparent',
      boxShadow: isHovered
        ? '0 16px 34px -14px color-mix(in oklab, var(--heat) 70%, transparent)'
        : '0 2px 10px -4px color-mix(in oklab, var(--heat) 55%, transparent)',
    },
  };

  return (
    <button
      {...rest}
      className={['hub-btn', className].filter(Boolean).join(' ')}
      data-variant={variant}
      // `aria-disabled` mirrors `disabled` so a screen reader announces the
      // state that is now also visible; `disabled` alone removes the button
      // from the accessibility tree entirely in some AT.
      aria-disabled={isDisabled || undefined}
      onMouseEnter={(e) => {
        setHover(true);
        rest.onMouseEnter?.(e);
      }}
      onMouseLeave={(e) => {
        setHover(false);
        rest.onMouseLeave?.(e);
      }}
      style={{
        ...base,
        ...variants[variant],
        ...(isDisabled ? disabledStyle : null),
        ...style,
      }}
    />
  );
}
